import { useAuth } from "@/composables/useAuth";
import type { BaseUser } from "#shared/types/index";
import { useCrypto } from "#shared/utils/useCrypto";
import { useRuntimeConfig } from "#app";

export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path === "/auth/session-collision") {
    return;
  }

  const { isAuthenticated, ready, serverReady, user, logout } = useAuth();

  const value = to.query.em;
  const first = Array.isArray(value) ? value[0] : value;
  const encryptedEmail = typeof first === "string" ? first : undefined;

  const config = useRuntimeConfig();
  const landingPageUrl = config.public.landingPageUrl;

  const publicRoutes = [
    "/",
    "/login",
    "/get-started/viewer",
    "/get-started-as",
    "/forgot-password",
    "/reset-password",
    "/otp-verification",
    "/contact",
    "/reset-password-success",
    "/auth/not-allowed",
    "/content/premieres/claim-access",
  ];

  const isPublicRoute = publicRoutes.includes(to.path);

  if (import.meta.server && serverReady.value) {
    await serverReady.value;
  }

  if (import.meta.client && !ready.value) {
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        console.warn("[Auth Middleware] Timeout waiting for auth ready");
        resolve();
      }, 3000);

      const unwatch = watch(
        ready,
        (isReady) => {
          if (isReady) {
            clearTimeout(timeout);
            unwatch();
            resolve();
          }
        },
        { immediate: true },
      );
    });
  }

  if (isAuthenticated.value && isPublicRoute) {
    if (to.path === "/" && to.query.redirect) {
      const redirectPath = to.query.redirect as string;
      return navigateTo(redirectPath);
    }

    if (
      (to.path.startsWith("/reset-password") ||
        to.path.startsWith("/creators/join")) &&
      encryptedEmail
    ) {
      const collisionRedirect = await processOnboardingLogic(
        user.value!,
        encryptedEmail,
        to,
      );
      if (collisionRedirect && collisionRedirect.path !== to.path) {
        return navigateTo(collisionRedirect);
      }
    }

    const authPages = [
      // "/",
      "/login",
      "/get-started/viewer",
      "/get-started-as",
      "/forgot-password",
      "/reset-password",
      "/otp-verification",
      "/contact",
      "/reset-password-success",
      "/auth/not-allowed",
      "/content/premieres/claim-access",
    ];

    if (authPages.includes(to.path)) {
      return navigateTo(landingPageUrl);
    }
  }

  if (!isAuthenticated.value && !isPublicRoute) {
    return navigateTo({
      path: "/",
      query: { redirect: to.fullPath },
    });
  }
});

async function processOnboardingLogic(
  user: BaseUser,
  encryptedEmail: string,
  to: any,
) {
  if (!user || import.meta.client) {
    return;
  }

  const { decrypt } = useCrypto();
  const config = useRuntimeConfig();
  const encryptKey = String(config.encryptionKey ?? "");

  const decryptedInvite = decrypt(encryptedEmail, encryptKey);

  if (decryptedInvite && decryptedInvite !== user.email) {
    console.warn(`[Auth] Collision: ${user.email} vs ${decryptedInvite}`);

    // IMPORTANT: Return this so the middleware can consume it
    return {
      path: "/auth/session-collision",
      query: {
        target: decryptedInvite,
        redirect: to.fullPath,
      },
    };
  }
}
